import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Gauge, ArrowUpRight, Sparkles, Cpu } from "lucide-react";
import { leadGenAudit } from "@/lib/api";

const tone = (v) => (v >= 75 ? "#16a34a" : v >= 50 ? "#d97706" : "#e11d48");
const impactStyle = {
  high: "bg-rose-50 text-rose-700 border-rose-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  low: "bg-slate-100 text-slate-600 border-slate-200",
};

function Gauge100({ score }) {
  const c = tone(score);
  const pct = Math.max(0, Math.min(100, score));
  return (
    <div className="relative grid h-36 w-36 place-items-center rounded-full"
      style={{ background: `conic-gradient(${c} ${pct * 3.6}deg, #e2e8f0 0deg)` }}>
      <div className="grid h-28 w-28 place-items-center rounded-full bg-white">
        <div className="font-heading text-4xl font-extrabold" style={{ color: c }}>{score}</div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">out of 100</div>
      </div>
    </div>
  );
}

/**
 * Lead Generation Readiness — 0-100 score + 7 subscores (4 measured, 3 AI-estimated)
 * + prioritized fixes. Pass either `report` (presentational) or `url` (self-fetch).
 */
export default function LeadGenReadiness({ report: initialReport = null, url = "", source = "leadgen-audit", accent = "#2563eb" }) {
  const [report, setReport] = useState(initialReport);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setReport(initialReport); }, [initialReport]);

  useEffect(() => {
    if (initialReport || !url) return;
    let alive = true;
    setLoading(true);
    leadGenAudit(url, source)
      .then((r) => { if (alive) setReport(r); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [url, initialReport, source]);

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white p-8 text-slate-500" data-testid="leadgen-readiness-loading">
        <Loader2 size={18} className="animate-spin" /> Scoring your lead-generation readiness…
      </div>
    );
  }
  if (!report) return null;

  const subs = report.subscores || [];
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8" data-testid="leadgen-readiness">
      <div className="flex flex-col items-center gap-6 md:flex-row md:items-center md:gap-8">
        <Gauge100 score={report.overall_score} />
        <div className="flex-1 text-center md:text-left">
          <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold"
            style={{ borderColor: `${tone(report.overall_score)}44`, color: tone(report.overall_score) }}>
            <Gauge size={13} /> Lead Generation Readiness · {report.band}
          </div>
          <h3 className="mt-3 font-heading text-2xl font-extrabold text-slate-900">
            How well your site turns visitors into customers
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">{report.summary}</p>
        </div>
      </div>

      {/* Subscores */}
      <div className="mt-8 grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2" data-testid="leadgen-subscores">
        {subs.map((s) => (
          <div key={s.key} data-testid={`leadgen-sub-${s.key}`}>
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-800">{s.label}</span>
                {s.measured ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700" title="Measured from your live site">
                    Measured
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700" title="AI estimate — not directly measurable from one page">
                    <Cpu size={10} /> AI estimate
                  </span>
                )}
              </div>
              <span className="font-heading text-sm font-bold" style={{ color: tone(s.score) }}>{s.score}</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full transition-all" style={{ width: `${s.score}%`, backgroundColor: tone(s.score) }} />
            </div>
            <p className="mt-1 text-xs text-slate-400">{s.blurb}</p>
          </div>
        ))}
      </div>

      <p className="mt-4 text-[11px] text-slate-400">
        <Cpu size={11} className="mr-1 inline" /> Lead Follow-Up, Automation and Analytics are AI estimates based on public signals — a full audit confirms them.
      </p>

      {/* Prioritized fixes */}
      {(report.fixes || []).length > 0 && (
        <div className="mt-8">
          <div className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-slate-900">
            <Sparkles size={18} style={{ color: accent }} /> Prioritized fixes to get more leads
          </div>
          <div className="space-y-2.5" data-testid="leadgen-fixes">
            {report.fixes.map((f, i) => (
              <Link key={i} to={f.url} data-testid={`leadgen-fix-${i}`}
                className="group flex items-start justify-between gap-4 rounded-xl border border-slate-200 p-4 transition-colors hover:border-slate-300 hover:bg-slate-50">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap ${impactStyle[f.impact] || impactStyle.medium}`}>{f.impact} impact</span>
                    <span className="font-semibold text-slate-900">{f.title}</span>
                  </div>
                  {f.why && <p className="mt-1 text-sm text-slate-500">{f.why}</p>}
                </div>
                <ArrowUpRight size={16} className="mt-1 shrink-0 text-slate-300 transition-colors group-hover:text-brand" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
